import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import type { NotificationGroup } from "./types";
import {
  GROUP_HEALTH_ACTIVE,
  GROUP_HEALTH_EMPTY,
  GROUP_HEALTH_NO_RECIPIENTS,
  GROUP_HEALTH_NO_RESOURCES,
  type GroupHealthFilterId,
} from "@redux/slices/notificationsGroupsFiltersSlice";

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export type GroupHealthStatus =
  | typeof GROUP_HEALTH_EMPTY
  | typeof GROUP_HEALTH_NO_RECIPIENTS
  | typeof GROUP_HEALTH_NO_RESOURCES
  | typeof GROUP_HEALTH_ACTIVE;

export const GROUP_HEALTH_LABEL: Record<GroupHealthStatus, string> = {
  [GROUP_HEALTH_EMPTY]: "Empty",
  [GROUP_HEALTH_NO_RECIPIENTS]: "No recipients",
  [GROUP_HEALTH_NO_RESOURCES]: "No resources",
  [GROUP_HEALTH_ACTIVE]: "Active",
};

export function computeGroupHealthStatus(g: NotificationGroup): GroupHealthStatus {
  const recipients = g.memberCount ?? 0;
  const resources = g.assignmentCount ?? 0;
  if (recipients === 0 && resources === 0) return GROUP_HEALTH_EMPTY;
  if (recipients === 0) return GROUP_HEALTH_NO_RECIPIENTS;
  if (resources === 0) return GROUP_HEALTH_NO_RESOURCES;
  return GROUP_HEALTH_ACTIVE;
}

export interface GroupRow extends NotificationGroup {
  healthStatus: GroupHealthStatus;
  healthLabel: string;
}

export function toGroupRow(g: NotificationGroup): GroupRow {
  const healthStatus = computeGroupHealthStatus(g);
  return {
    ...g,
    healthStatus,
    healthLabel: GROUP_HEALTH_LABEL[healthStatus],
  };
}

export function matchesStatusSelection(
  row: GroupRow,
  selection: GroupHealthFilterId[]
): boolean {
  if (selection.length === 0) return true;
  return selection.includes(row.healthStatus as GroupHealthFilterId);
}

export const groupsColumns: GenericTableColumn<GroupRow>[] = [
  {
    header: "Group",
    accessor: "name",
    sortAccessor: (row) => row.name.toLowerCase(),
    renderCell: (row) => (
      <div className="flex items-center gap-2 min-w-0">
        <span
          aria-hidden
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{
            background: row.color ?? "var(--color-primitive-gray-300)",
          }}
        />
        <span className="font-medium text-semantic-text-primary truncate">
          {row.name}
        </span>
      </div>
    ),
  },
  {
    header: "Description",
    accessor: "description",
    renderCell: (row) =>
      row.description ? (
        <span className="text-semantic-text-secondary truncate block max-w-md">
          {row.description}
        </span>
      ) : (
        <span className="italic text-semantic-text-secondary">—</span>
      ),
  },
  {
    header: "Status",
    sortAccessor: (row) => row.healthStatus,
    renderCell: (row) => (
      <GenericTag
        label={row.healthLabel}
        variant={row.healthStatus === GROUP_HEALTH_ACTIVE ? "success" : "warning"}
        size="small"
      />
    ),
  },
  {
    header: "Recipients",
    sortAccessor: (row) => row.memberCount ?? 0,
    renderCell: (row) => (
      <span className="text-semantic-text-primary font-medium">
        {row.memberCount ?? 0}
      </span>
    ),
  },
  {
    header: "Resources",
    sortAccessor: (row) => row.assignmentCount ?? 0,
    renderCell: (row) => (
      <span className="text-semantic-text-primary font-medium">
        {row.assignmentCount ?? 0}
      </span>
    ),
  },
  {
    header: "Last updated",
    sortAccessor: (row) => new Date(row.updatedAt).getTime(),
    renderCell: (row) => (
      <span className="text-semantic-text-secondary text-sm">
        {formatDate(row.updatedAt)}
      </span>
    ),
  },
];
